import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material'
import useUndertakingFormRepository from '../../../repositories/UndertakingForm/use-undertaking-form-repository'
import { UndertakingFormConfig } from '../../../repositories/UndertakingForm/types'
import { useAuthenticatedGuard } from '../../../router/guards/authenticated-guard'
import SplashScreenGuard from '../../../router/guards/SplashScreenGuard'
import CommonBackButton from '../../atoms/CommonBackButton/CommonBackButton'
import MarkdownContent from '../../atoms/MarkdownContent/MarkdownContent'
import ConfirmationDialog from '../../molecules/ConfirmationDialog/ConfirmationDialog'
import { Route, STRAPI_URL } from '../../../router'
import LocalStorageKeys from '../../../types/LocalStorageKeys'
import { MyInfoReason, MyInfoUserDataContent } from '../../../types/MyInfoUserData'
import useTrackPageView from '../../../helpers/hooks/use-track-page-view'
import { isValidSingaporePhoneNumber, isValidEmail } from '../../../helpers/validation/validation-utils'
import retrieveMyInfoButtonImage from '../../../assets/retrieve-with-my-info-button.png'

import styles from './UndertakingFormPage.module.scss'

const UndertakingFormPage = () => {
    useTrackPageView(`Undertaking Form Page`)
    useAuthenticatedGuard(Route.UNDERTAKING_FORM)

    const navigate = useNavigate()
    const { retrieveFormConfig, retrieveSubmissionStatus, submitForm } = useUndertakingFormRepository()

    // State
    const [config, setConfig] = useState<UndertakingFormConfig | null>(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Form data
    const [formData, setFormData] = useState({
        nric: ``,
        fullName: ``,
        mobileNumber: ``,
        emailAddress: ``,
        dateOfBirth: ``,
        employmentStatus: ``,
        companyName: ``,
        unemployedStatus: ``,
        postalCode: ``,
        block: ``,
        street: ``,
        floor: ``,
        unit: ``,
        building: ``,
    })

    const [hasAgreed, setHasAgreed] = useState<boolean | null>(null)
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
    const [showDisagreeDialog, setShowDisagreeDialog] = useState(false)
    const [showAlreadySubmittedDialog, setShowAlreadySubmittedDialog] = useState(false)
    const [previouslyAgreed, setPreviouslyAgreed] = useState(false)
    const [showInactiveMemberDialog, setShowInactiveMemberDialog] = useState(false)
    const [showFaqDialog, setShowFaqDialog] = useState(false)
    const [showTncDialog, setShowTncDialog] = useState(false)

    useEffect(() => {
        loadFormData()
    }, [])

    const loadFormData = async () => {
        setLoading(true)
        setError(null) // Clear any previous errors

        // Check if returning from MyInfo first
        const myinfoDataStr = localStorage.getItem(LocalStorageKeys.MYINFO_DATA)

        // Load config
        const configResult = await retrieveFormConfig()
        if (configResult.data) {
            setConfig(configResult.data)
        } else if (configResult.error) {
            console.error(`Failed to load config:`, configResult.error)
            setError(`Failed to load form configuration. Please ensure the undertaking form config has been created in Strapi admin.`)
            setLoading(false)
            return
        }

        // Load submission status
        const statusResult = await retrieveSubmissionStatus()
        if (statusResult.data) {
            // If previously submitted
            if (statusResult.data.hasSubmitted) {
                if (statusResult.data.hasDisagreed) {
                    // User disagreed - show message but allow resubmission
                    // However, don't show dialog if user is returning from MyInfo (resubmitting)
                    if (!myinfoDataStr) {
                        setShowAlreadySubmittedDialog(true)
                        setPreviouslyAgreed(false)
                    }
                } else {
                    // User agreed - show message and will redirect
                    setShowAlreadySubmittedDialog(true)
                    setPreviouslyAgreed(true)
                    setLoading(false)
                    return
                }
            }
        } else if (statusResult.error) {
            // Check if error is due to inactive membership
            if (statusResult.error.message?.includes(`Only active members`) ||
                statusResult.error.message?.includes(`active members can access`)) {
                setShowInactiveMemberDialog(true)
                setLoading(false)
                return
            }
            // Handle other errors
            console.error(`Failed to load submission status:`, statusResult.error)
        }

        // Check if returning from MyInfo and populate form
        if (myinfoDataStr) {
            try {
                const myinfoData = JSON.parse(myinfoDataStr)
                populateFormFromMyInfo(myinfoData)
                // Automatically set hasAgreed to true since user fetched MyInfo
                setHasAgreed(true)
                // Clear the MyInfo data and flag after using it
                localStorage.removeItem(LocalStorageKeys.MYINFO_DATA)
                localStorage.removeItem(LocalStorageKeys.UNDERTAKING_FORM_IN_PROGRESS)
            } catch (err) {
                console.error(`Failed to parse MyInfo data:`, err)
                // Clear corrupted data
                localStorage.removeItem(LocalStorageKeys.MYINFO_DATA)
                localStorage.removeItem(LocalStorageKeys.UNDERTAKING_FORM_IN_PROGRESS)
            }
        }

        setLoading(false)
    }

    const populateFormFromMyInfo = (myinfoData: MyInfoUserDataContent) => {
        setFormData(prev => ({
            ...prev,
            nric: myinfoData.uinFin || prev.nric,
            fullName: myinfoData.name || prev.fullName,
            mobileNumber: myinfoData.phoneNumber || prev.mobileNumber,
            emailAddress: myinfoData.email || prev.emailAddress,
            dateOfBirth: myinfoData.dob ? formatDateForInput(myinfoData.dob) : prev.dateOfBirth,
            postalCode: myinfoData.postalCode || prev.postalCode,
            block: myinfoData.block || prev.block,
            street: myinfoData.street || prev.street,
            floor: myinfoData.floor || prev.floor,
            unit: myinfoData.unit || prev.unit,
            building: myinfoData.building || prev.building,
        }))
    }

    const formatDateForInput = (dateStr: string): string => {
        // MyInfo date format is typically YYYY-MM-DD or DD/MM/YYYY
        // HTML date input expects YYYY-MM-DD
        try {
            if (dateStr.includes(`/`)) {
                // Convert DD/MM/YYYY to YYYY-MM-DD
                const parts = dateStr.split(`/`)
                const expectedDateParts = 3
                const yearIndex = 2
                const monthIndex = 1
                const dayIndex = 0
                const paddingLength = 2
                const paddingChar = `0`
                if (parts.length === expectedDateParts) {
                    return `${parts[yearIndex]}-${parts[monthIndex].padStart(paddingLength, paddingChar)}-${parts[dayIndex].padStart(paddingLength, paddingChar)}`
                }
            }
            // Already in YYYY-MM-DD format or invalid
            return dateStr
        } catch (err) {
            console.error(`Failed to convert date format:`, err)
            return dateStr
        }
    }

    const handleMyInfoFetch = () => {
        // Store form state in localStorage before MyInfo redirect
        localStorage.setItem(LocalStorageKeys.UNDERTAKING_FORM_IN_PROGRESS, `true`)
        localStorage.setItem(LocalStorageKeys.MYINFO_REASON, MyInfoReason.SIGNUP)
        globalThis.location.href = `${STRAPI_URL}${Route.MYINFO_AUTHORISE}?reason=${MyInfoReason.SIGNUP}`
    }

    const handleFormDataChange = (field: string, value: string) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value }
            // Clear dependent fields when employment status changes
            if (field === `employmentStatus`) {
                updated.companyName = ``
                updated.unemployedStatus = ``
            }
            return updated
        })
        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                // Also clear dependent field errors
                if (field === `employmentStatus`) {
                    delete newErrors.companyName
                    delete newErrors.unemployedStatus
                }
                return newErrors
            })
        }
    }

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {}

        // Common required fields for both agree and disagree
        if (!formData.fullName.trim()) {
            errors.fullName = `Full name is required`
        }
        if (!isValidSingaporePhoneNumber(formData.mobileNumber)) {
            errors.mobileNumber = `Please enter a valid Singapore mobile number`
        }
        if (!isValidEmail(formData.emailAddress)) {
            errors.emailAddress = `Please enter a valid email address`
        }

        // Additional fields required for agreement
        if (hasAgreed === true) {
            if (!formData.nric.trim()) {
                errors.nric = `NRIC is required when agreeing`
            }
            if (!formData.dateOfBirth.trim()) {
                errors.dateOfBirth = `Date of birth is required when agreeing`
            }
            if (!formData.employmentStatus.trim()) {
                errors.employmentStatus = `Employment status is required when agreeing`
            }
            if (formData.employmentStatus === `employed` && !formData.companyName.trim()) {
                errors.companyName = `Company name is required`
            }
            if (formData.employmentStatus === `unemployed` && !formData.unemployedStatus.trim()) {
                errors.unemployedStatus = `Unemployed status is required`
            }
            if (!formData.postalCode.trim()) {
                errors.postalCode = `Postal code is required when agreeing`
            }
            if (!formData.block.trim()) {
                errors.block = `Block is required when agreeing`
            }
            if (!formData.street.trim()) {
                errors.street = `Street is required when agreeing`
            }
        }

        setValidationErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleSubmit = async () => {
        if (hasAgreed === null) {
            setError(`Please select either "I agree" or "I disagree"`)
            return
        }

        if (!validateForm()) {
            setError(`Please fill in all required fields`)
            return
        }

        if (hasAgreed === false) {
            // Show confirmation dialog for disagreement
            setShowDisagreeDialog(true)
            return
        }

        await performSubmission()
    }

    const performSubmission = async () => {
        setSubmitting(true)
        setError(null)
        setShowDisagreeDialog(false)

        // Map frontend field names to backend expected field names
        // Only include non-empty values to avoid sending empty strings for optional fields
        const mappedFormData: {
            name: string
            mobileNumber: string
            emailAddress: string
            nric?: string
            dateOfBirth?: string
            employmentStatus?: string
            companyName?: string
            unemployedStatus?: string
            postalCode?: string
            block?: string
            street?: string
            floor?: string
            unit?: string
            building?: string
        } = {
            name: formData.fullName, // Backend expects 'name' not 'fullName'
            mobileNumber: formData.mobileNumber,
            emailAddress: formData.emailAddress,
        }

        // Add optional fields only if they have values
        if (formData.nric.trim()){
            mappedFormData.nric = formData.nric
        }
        if (formData.dateOfBirth.trim()){
            mappedFormData.dateOfBirth = formData.dateOfBirth
        }
        if (formData.employmentStatus.trim()){
            mappedFormData.employmentStatus = formData.employmentStatus
        }
        if (formData.companyName.trim()){
            mappedFormData.companyName = formData.companyName
        }
        if (formData.unemployedStatus.trim()){
            mappedFormData.unemployedStatus = formData.unemployedStatus
        }
        if (formData.postalCode.trim()){
            mappedFormData.postalCode = formData.postalCode
        }
        if (formData.block.trim()){
            mappedFormData.block = formData.block
        }
        if (formData.street.trim()){
            mappedFormData.street = formData.street
        }
        if (formData.floor.trim()){
            mappedFormData.floor = formData.floor
        }
        if (formData.unit.trim()){
            mappedFormData.unit = formData.unit
        }
        if (formData.building.trim()){
            mappedFormData.building = formData.building
        }

        const result = await submitForm({
            hasDisagreedToUndertaking: hasAgreed === false,
            responses: mappedFormData,
        })

        setSubmitting(false)

        if (result.data) {
            // Store whether user agreed or disagreed
            localStorage.setItem(LocalStorageKeys.UNDERTAKING_FORM_HAS_AGREED, hasAgreed ? `true` : `false`)

            // Store refund data if disagreed
            if (result.data.submission.hasDisagreedToUndertaking) {
                localStorage.setItem(
                    LocalStorageKeys.UNDERTAKING_FORM_REFUND,
                    JSON.stringify({
                        amount: result.data.submission.refundAmount,
                        months: result.data.submission.refundMonths,
                    }),
                )
            }
            navigate(Route.UNDERTAKING_FORM_THANK_YOU)
        } else if (result.error) {
            setError(result.error.message || `Failed to submit form. Please try again.`)
        }
    }

    const handleAlreadySubmittedDialogClose = () => {
        setShowAlreadySubmittedDialog(false)
        if (previouslyAgreed) {
            navigate(Route.HOME)
        }
    }

    const handleInactiveMemberDialogClose = () => {
        setShowInactiveMemberDialog(false)
        navigate(Route.HOME)
    }

    const handleSkip = () => {
        localStorage.setItem(LocalStorageKeys.UNDERTAKING_FORM_SKIPPED, `true`)
        navigate(Route.HOME)
    }

    if (loading) {
        return (
            <SplashScreenGuard>
                <Container className={styles.loadingContainer}>
                    <CircularProgress />
                </Container>
            </SplashScreenGuard>
        )
    }

    return (
        <SplashScreenGuard>
            <Container className={styles.container}>
                <CommonBackButton path={Route.HOME} />
                <Typography variant="h3" className={styles.title}>
                    {config?.title || `Undertaking Form`}
                </Typography>
                {error && (
                    <Alert severity="error" className={styles.alert} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {config?.undertakingContent && (
                    <Box className={styles.content}>
                        <MarkdownContent content={config.undertakingContent} />
                    </Box>
                )}

                <Box className={styles.compulsoryFormFields}>
                    <TextField
                        label="Full Name"
                        value={formData.fullName}
                        onChange={(e) => handleFormDataChange(`fullName`, e.target.value)}
                        required
                        fullWidth
                        error={!!validationErrors.fullName}
                        helperText={validationErrors.fullName}
                        className={styles.field}
                    />

                    <TextField
                        label="Mobile Number"
                        value={formData.mobileNumber}
                        onChange={(e) => handleFormDataChange(`mobileNumber`, e.target.value)}
                        required
                        fullWidth
                        error={!!validationErrors.mobileNumber}
                        helperText={validationErrors.mobileNumber}
                        className={styles.field}
                    />

                    <TextField
                        label="Email Address"
                        type="email"
                        value={formData.emailAddress}
                        onChange={(e) => handleFormDataChange(`emailAddress`, e.target.value)}
                        required
                        fullWidth
                        error={!!validationErrors.emailAddress}
                        helperText={validationErrors.emailAddress}
                        className={styles.field}
                    />
                </Box>

                {config?.formInfo && (
                    <Box className={styles.content}>
                        <MarkdownContent content={config.formInfo} />
                    </Box>
                )}

                <Box mb={2}>
                    <Typography variant="body2">
                        Read the full underwriting form terms and conditions{` `}
                        <Typography
                            component="span"
                            onClick={() => setShowTncDialog(true)}
                            style={{ color: `#1976d2`, cursor: `pointer`, textDecoration: `underline` }}
                        >
                            here
                        </Typography>
                        .
                    </Typography>
                </Box>
                <Box mb={2}>
                    <Typography variant="body2">
                        You can also read more about the updates to Starter Membership{` `}
                        <Typography
                            component="span"
                            onClick={() => setShowFaqDialog(true)}
                            style={{ color: `#1976d2`, cursor: `pointer`, textDecoration: `underline` }}
                        >
                            here
                        </Typography>
                        .
                    </Typography>
                </Box>

                <Box className={styles.radioSection}>
                    <Typography variant="body1" fontWeight="bold" mb={1}>
                        Do you agree to transition to NTUC Membership?
                    </Typography>
                    <RadioGroup
                        value={hasAgreed === null ? `` : (hasAgreed ? `agree` : `disagree`)}
                        onChange={(e) => setHasAgreed(e.target.value === `agree`)}
                    >
                        <FormControlLabel value="agree" control={<Radio />} label="I agree" />
                        <FormControlLabel value="disagree" control={<Radio />} label="I disagree" />
                    </RadioGroup>
                </Box>

                {(config?.provisionContent && hasAgreed === true) && (
                    <Box className={styles.content}>
                        <MarkdownContent content={config.provisionContent} />
                    </Box>
                )}

                {hasAgreed === true && (<Box className={styles.myInfoSection}>
                    <Typography variant="body1" className={styles.myInfoText}>
                        Use MyInfo to auto-fill your details:
                    </Typography>
                    <Box
                        component="img"
                        onClick={handleMyInfoFetch}
                        className={styles.myInfoButton}
                        src={retrieveMyInfoButtonImage}
                        alt="Retrieve with MyInfo"
                        sx={{ cursor: submitting ? `not-allowed` : `pointer`, opacity: 1 }}
                    />
                </Box> )}

                {hasAgreed === true && ( <Box className={styles.formFields}>
                    <TextField
                        label="NRIC/FIN"
                        value={formData.nric}
                        disabled
                        fullWidth
                        error={!!validationErrors.nric}
                        className={styles.field}
                    />

                    <TextField
                        label="Date of Birth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleFormDataChange(`dateOfBirth`, e.target.value)}
                        fullWidth
                        error={!!validationErrors.dateOfBirth}
                        helperText={validationErrors.dateOfBirth}
                        className={styles.field}
                        InputLabelProps={{ shrink: true }}
                    />

                    <FormControl
                        fullWidth
                        error={!!validationErrors.employmentStatus}
                        className={styles.field}
                    >
                        <InputLabel id="employment-status-label">Employment Status *</InputLabel>
                        <Select
                            labelId="employment-status-label"
                            value={formData.employmentStatus}
                            onChange={(e) => handleFormDataChange(`employmentStatus`, e.target.value)}
                            label="Employment Status *"
                        >
                            <MenuItem value="employed">Employed</MenuItem>
                            <MenuItem value="unemployed">Unemployed</MenuItem>
                        </Select>
                        {validationErrors.employmentStatus && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                {validationErrors.employmentStatus}
                            </Typography>
                        )}
                    </FormControl>

                    {formData.employmentStatus === `employed` && (
                        <TextField
                            label="Company Name"
                            placeholder="Company name"
                            value={formData.companyName}
                            onChange={(e) => handleFormDataChange(`companyName`, e.target.value)}
                            required
                            fullWidth
                            error={!!validationErrors.companyName}
                            helperText={validationErrors.companyName}
                            className={styles.field}
                        />
                    )}

                    {formData.employmentStatus === `unemployed` && (
                        <FormControl
                            fullWidth
                            error={!!validationErrors.unemployedStatus}
                            className={styles.field}
                        >
                            <InputLabel id="unemployed-status-label">Unemployed Status *</InputLabel>
                            <Select
                                labelId="unemployed-status-label"
                                value={formData.unemployedStatus}
                                onChange={(e) => handleFormDataChange(`unemployedStatus`, e.target.value)}
                                label="Unemployed Status *"
                            >
                                <MenuItem value="studying">Studying</MenuItem>
                                <MenuItem value="graduating-this-year">Graduating This Year</MenuItem>
                                <MenuItem value="graduated">Graduated</MenuItem>
                            </Select>
                            {validationErrors.unemployedStatus && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                    {validationErrors.unemployedStatus}
                                </Typography>
                            )}
                        </FormControl>
                    )}

                    <TextField
                        label="Postal Code"
                        value={formData.postalCode}
                        onChange={(e) => handleFormDataChange(`postalCode`, e.target.value)}
                        fullWidth
                        error={!!validationErrors.postalCode}
                        helperText={validationErrors.postalCode}
                        className={styles.field}
                    />

                    <TextField
                        label="Block"
                        value={formData.block}
                        onChange={(e) => handleFormDataChange(`block`, e.target.value)}
                        fullWidth
                        error={!!validationErrors.block}
                        helperText={validationErrors.block}
                        className={styles.field}
                    />

                    <TextField
                        label="Street"
                        value={formData.street}
                        onChange={(e) => handleFormDataChange(`street`, e.target.value)}
                        fullWidth
                        error={!!validationErrors.street}
                        helperText={validationErrors.street}
                        className={styles.field}
                    />

                    <TextField
                        label="Floor"
                        value={formData.floor}
                        onChange={(e) => handleFormDataChange(`floor`, e.target.value)}
                        fullWidth
                        className={styles.field}
                    />

                    <TextField
                        label="Unit"
                        value={formData.unit}
                        onChange={(e) => handleFormDataChange(`unit`, e.target.value)}
                        fullWidth
                        className={styles.field}
                    />

                    <TextField
                        label="Building"
                        value={formData.building}
                        onChange={(e) => handleFormDataChange(`building`, e.target.value)}
                        fullWidth
                        className={styles.field}
                    />
                </Box>)}

                <Box className={styles.actions}>
                    <Button
                        variant="outlined"
                        onClick={handleSkip}
                        disabled={submitting}
                        className={styles.skipButton}
                    >
                        Skip for now
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={hasAgreed === null || submitting}
                        className={styles.submitButton}
                    >
                        {submitting ? <CircularProgress size={24} /> : `Submit`}
                    </Button>
                </Box>

                {/* Disagreement Confirmation Dialog */}
                <Dialog
                    open={showDisagreeDialog}
                    onClose={() => !submitting && setShowDisagreeDialog(false)}
                >
                    <DialogTitle>Confirm Disagreement</DialogTitle>
                    <DialogContent>
                        {config?.disagreeConfirmationContent && (
                            <MarkdownContent content={config.disagreeConfirmationContent} />
                        )}
                    </DialogContent>
                    <DialogActions>
                        {hasAgreed === true && (<Button
                            onClick={() => setShowDisagreeDialog(false)}
                            disabled={submitting}
                        >
                            Go Back
                        </Button>)}
                        <Button
                            onClick={performSubmission}
                            color="primary"
                            variant="contained"
                            disabled={submitting}
                        >
                            {submitting ? <CircularProgress size={24} /> : `Submit`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Already Submitted Dialog */}
                <Dialog
                    open={showAlreadySubmittedDialog}
                    onClose={handleAlreadySubmittedDialogClose}
                >
                    <DialogTitle>
                        {previouslyAgreed ? `Application Already Sent` : `Previous Submission Found`}
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            {previouslyAgreed ? (
                                <>
                                    You have already submitted your underwriting response.
                                    You have previously responded to the membership transition and need not take any further action for now.
                                </>
                            ) : (
                                <>
                                    You have previously submitted your underwriting response.
                                    To review your submission and agree to take up NTUC Membership, resubmit your response now.
                                </>
                            )}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleAlreadySubmittedDialogClose}
                            color="primary"
                            variant="contained"
                        >
                            {previouslyAgreed ? `OK` : `Continue`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Inactive Member Dialog */}
                <Dialog
                    open={showInactiveMemberDialog}
                    onClose={handleInactiveMemberDialogClose}
                >
                    <DialogTitle>Active Membership Required</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            Only active members can access the undertaking form. Please ensure your membership is active before attempting to submit this form.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleInactiveMemberDialogClose}
                            color="primary"
                            variant="contained"
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Terms & Conditions Dialog */}
                <ConfirmationDialog
                    acknowledgeOnly
                    buttonText="Close"
                    confirmationTitle="Terms & Conditions"
                    confirmationText={config?.termsAndConditions || ``}
                    defaultOpen={showTncDialog}
                    onConfirm={() => setShowTncDialog(false)}
                />

                {/* Updates to Starter Membership Dialog */}
                <ConfirmationDialog
                    acknowledgeOnly
                    buttonText="Close"
                    confirmationTitle="Updates to Starter Membership"
                    confirmationText={config?.faq || ``}
                    defaultOpen={showFaqDialog}
                    onConfirm={() => setShowFaqDialog(false)}
                />
            </Container>
        </SplashScreenGuard>
    )
}

export default UndertakingFormPage
